import QRCode from "qrcode";
import { useEffect, useMemo, useState } from "react";
import {
  consultCmfRedecDebt,
  createRedecAccess,
  createRedecConsentAction,
  downloadRedecReceiptPdf,
  getAgreement,
  getComplianceStatuses,
  getEntitiesByUserReference,
  getRedecConsentAction,
} from "./soyioApi";
import type {
  Agreement,
  ApiResult,
  CmfRedecDebtPayload,
  ConsentAction,
} from "./soyioApi";
import {
  destroyConsentBox,
  mountConsentBox,
  type ConsentCheckboxChange,
} from "./soyioWidget";

const REDEC_PRODUCT_ID = "prod_xelOf1tIYV8t-ZM6fAe1iA";
const REDEC_CONSENT_TEMPLATE_ID =
  process.env.REACT_APP_SOYIO_REDEC_CONSENT_TEMPLATE_ID ??
  "constpl_NxKFQ5R8fdp04wURsIHrmw";
const USER_REFERENCE_BASE =
  process.env.REACT_APP_CANOPY_USER_REFERENCE ?? "loloman_el_pulento";
const CMF_CODIGO_INSTITUCION =
  process.env.REACT_APP_CMF_REDEC_CODIGO_INSTITUCION ?? "12345678K";

type Stage = "profile" | "consent" | "processing" | "result";
type EvidenceStatus = "pending" | "running" | "success" | "error";
type ProcessingPhase = "running" | "success";
type EvidenceStepId =
  | "entity"
  | "register"
  | "consent_action"
  | "agreement"
  | "receipt"
  | "compliance"
  | "cmf"
  | "access";

type DemoProfile = {
  id: string;
  rut: string;
  name: string;
  description: string;
  expected: string;
  tone: "low" | "medium" | "high" | "none";
};

type DebtRow = {
  periodo: string;
  tipoCredito: string;
  tipoDeudor: string;
  operaciones: number | null;
  deudaVigente: number | null;
  montoAlDia: number | null;
  mora: number;
  moraMaxima: number | null;
};

type CmfRetryContext = {
  action: ConsentAction;
  payload: CmfRedecDebtPayload;
};

type EvidenceStep = {
  id: EvidenceStepId;
  label: string;
  description: string;
  status: EvidenceStatus;
  result?: ApiResult;
};

type ProcessingMilestone = {
  label: string;
  ids: EvidenceStepId[];
  status: EvidenceStatus;
};

const PROFILES: DemoProfile[] = [
  {
    id: "low",
    rut: "444444444",
    name: "Deuda baja",
    description: "Perfil liviano para una primera evaluación digital.",
    expected: "Sandbox: 4 registros en 2 periodos, sin mora.",
    tone: "low",
  },
  {
    id: "medium",
    rut: "555555555",
    name: "Deuda media",
    description: "Historial anual con mora moderada.",
    expected: "Sandbox: 36 registros, 12 periodos y mora moderada.",
    tone: "medium",
  },
  {
    id: "high",
    rut: "777777777",
    name: "Deuda alta",
    description: "Caso exigente para validar trazabilidad completa.",
    expected: "Sandbox: 180 registros, 36 periodos y deuda alta.",
    tone: "high",
  },
  {
    id: "none",
    rut: "999999999",
    name: "Sin deuda esperada",
    description: "Caso de prueba para respuesta sin registros.",
    expected: "Sandbox: CMF responde NOT_FOUND sin deuda.",
    tone: "none",
  },
];

const EVIDENCE_TEMPLATE: EvidenceStep[] = [
  {
    id: "entity",
    label: "Validar entidad",
    description: "GET /api/v1/entities",
    status: "pending",
  },
  {
    id: "register",
    label: "Registrar consentimiento",
    description: "POST /api/v1/redec/consent_actions",
    status: "pending",
  },
  {
    id: "consent_action",
    label: "Consultar consentimiento",
    description: "GET /api/v1/redec/consent_actions/:id",
    status: "pending",
  },
  {
    id: "agreement",
    label: "Consultar agreement",
    description: "GET /api/v1/agreements/:id",
    status: "pending",
  },
  {
    id: "receipt",
    label: "Preparar recibo PDF/hash",
    description: "GET /download_receipt",
    status: "pending",
  },
  {
    id: "compliance",
    label: "Validar compliance",
    description: "GET /agreement/compliance_statuses",
    status: "pending",
  },
  {
    id: "cmf",
    label: "Consultar deuda CMF",
    description: "Mock de la consulta de deuda CMF en sandbox local",
    status: "pending",
  },
  {
    id: "access",
    label: "Registrar acceso RDC31",
    description: "POST /api/v1/redec/accesses",
    status: "pending",
  },
];

const PROCESSING_MILESTONES: Array<Omit<ProcessingMilestone, "status">> = [
  {
    label: "Registrar consentimiento REDEC",
    ids: ["entity", "register", "consent_action"],
  },
  {
    label: "Consultar agreement",
    ids: ["agreement"],
  },
  {
    label: "Preparar recibo y hash",
    ids: ["receipt"],
  },
  {
    label: "Validar compliance",
    ids: ["compliance"],
  },
  {
    label: "Consulta deuda redec en APICMF",
    ids: ["cmf"],
  },
  {
    label: "Registrar acceso RDC31",
    ids: ["access"],
  },
];

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function formatCmfDate(date: Date): string {
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
    date.getDate()
  )}`;
}

function formatCmfTime(date: Date): string {
  return `${pad(date.getHours())}${pad(date.getMinutes())}${pad(
    date.getSeconds()
  )}`;
}

function formatUtcDate(date: Date): string {
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
    date.getUTCDate()
  )}`;
}

function formatUtcTime(date: Date): string {
  return `${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
    date.getUTCSeconds()
  )}`;
}

function normalizeRut(value: string): string {
  return value.replace(/[.\-\s]/g, "").toUpperCase();
}

function userReferenceForRut(rut: string): string {
  return `${USER_REFERENCE_BASE}_${normalizeRut(rut).toLowerCase()}`;
}

function retryUserReferenceForRut(rut: string): string {
  return `${userReferenceForRut(rut)}_${Date.now()}`;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function accessFromConsentCreatedAt(createdAt?: string) {
  if (!createdAt) return null;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return null;

  const access = new Date(created);
  access.setSeconds(access.getSeconds() + 1);
  return {
    date: formatUtcDate(access),
    time: formatUtcTime(access),
  };
}

function getAgreementExpiresAt(agreement?: Agreement): string | undefined {
  const permissions = Array.isArray(agreement?.data_permissions)
    ? agreement?.data_permissions ?? []
    : [];
  const productPermission = permissions.find(
    (permission) =>
      permission.scope_type === "product" &&
      permission.scope_id === REDEC_PRODUCT_ID &&
      typeof permission.expires_at === "string"
  );
  const fallbackPermission = permissions.find(
    (permission) => typeof permission.expires_at === "string"
  );
  return (productPermission?.expires_at ??
    fallbackPermission?.expires_at) as string | undefined;
}

function agreementDurationSummary(agreement: Agreement | null): string {
  const expiresAt = getAgreementExpiresAt(agreement ?? undefined);
  if (!expiresAt) return "Según vigencia configurada en Soyio";
  return `Vigente hasta ${formatReceiptDateTime(expiresAt)}`;
}

function numberAt(values: unknown[], index: number): number | null {
  const value = values[index];
  return typeof value === "number" ? value : null;
}

function stringAt(values: unknown[], index: number): string {
  const value = values[index];
  return typeof value === "string" ? value : "-";
}

function debtTypeLabel(value: number | null): string {
  if (value === 1) return "Comercial";
  if (value === 2) return "Consumo";
  if (value === 3) return "Hipotecario";
  return value === null ? "-" : `Tipo ${value}`;
}

function debtorTypeLabel(value: number | null): string {
  if (value === 1) return "Directo";
  if (value === 2) return "Indirecto";
  return "-";
}

function moraTotal(values: unknown[]): number {
  return values
    .slice(15, 24)
    .reduce<number>(
      (total, value) => total + (typeof value === "number" ? value : 0),
      0
    );
}

function getDebtRows(value: unknown): DebtRow[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is unknown[] => Array.isArray(item))
    .map((item) => ({
      periodo: stringAt(item, 0),
      tipoCredito: debtTypeLabel(numberAt(item, 3)),
      tipoDeudor: debtorTypeLabel(numberAt(item, 4)),
      operaciones: numberAt(item, 5),
      deudaVigente: numberAt(item, 6),
      montoAlDia: numberAt(item, 14),
      mora: moraTotal(item),
      moraMaxima: numberAt(item, 24),
    }));
}

function debtSummary(response: unknown) {
  const r = response as {
    status?: boolean;
    message?: string;
    subcode?: number;
    snapshot_metadata?: {
      captured_at?: string;
      captured_with?: string;
      presented_at?: string;
      runtime_source?: string;
      request_dates?: {
        fecha_inicio_otorgamiento?: string;
        hora_inicio_otorgamiento?: string;
        fecha_fin_consentimiento?: string;
        hora_fin_consentimiento?: string;
      };
    };
    response?: {
      resumen?: Record<string, unknown>;
      deudas?: unknown[];
    };
  };
  const resumen = r.response?.resumen ?? {};
  const rows = getDebtRows(r.response?.deudas);
  return {
    status: r.status,
    message: r.message,
    subcode: r.subcode,
    rows,
    totalRegistros:
      (resumen.total_registros as number | undefined) ?? rows.length,
    totalPeriodos: resumen.total_periodos as number | undefined,
    rut: resumen.rut as string | undefined,
    periodoInicio: resumen.periodo_inicio as string | undefined,
    periodoFin: resumen.periodo_fin as string | undefined,
    deuda: resumen.monto_total_deuda_vigente as number | undefined,
    mora: resumen.monto_total_mora_acumulada as number | undefined,
    operaciones: resumen.cantidad_operaciones_vigentes as number | undefined,
    periodosConMora: resumen.periodos_con_mora as number | undefined,
    snapshot: r.snapshot_metadata,
  };
}

function formatMoney(value: unknown): string {
  return typeof value === "number" ? `$${value.toLocaleString("es-CL")}` : "-";
}

function formatNumber(value: unknown): string {
  return typeof value === "number" ? value.toLocaleString("es-CL") : "-";
}

function formatReceiptDateTime(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

function isCompliant(response: unknown): boolean {
  const list = (response as { compliance_statuses?: unknown })
    .compliance_statuses;
  const statuses = Array.isArray(list)
    ? (list as Array<Record<string, unknown>>)
    : [];
  return statuses.some((item) => item.status === "complies");
}

function apiErrorMessages(result: ApiResult): string[] {
  const response = result.response as {
    detail?: string;
    errors?: unknown;
    title?: string;
  };
  const errors = Array.isArray(response?.errors)
    ? response.errors.filter((item): item is string => typeof item === "string")
    : [];
  return [
    ...errors,
    ...(response?.detail ? [response.detail] : []),
    ...(response?.title ? [response.title] : []),
  ];
}

function hasNinMismatch(result: ApiResult): boolean {
  return apiErrorMessages(result).some((message) =>
    message.toLowerCase().includes("entity nin does not match")
  );
}

function isReceiptPending(result: ApiResult): boolean {
  return (
    result.status === 404 &&
    apiErrorMessages(result).some((message) =>
      message.toLowerCase().includes("receipt pdf not available yet")
    )
  );
}

function getMilestoneStatus(
  ids: EvidenceStepId[],
  steps: EvidenceStep[]
): EvidenceStatus {
  const matchingSteps = ids
    .map((id) => steps.find((step) => step.id === id))
    .filter((step): step is EvidenceStep => Boolean(step));

  if (matchingSteps.some((step) => step.status === "error")) return "error";
  if (matchingSteps.every((step) => step.status === "success")) {
    return "success";
  }
  if (matchingSteps.some((step) => step.status === "running")) {
    return "running";
  }
  return "pending";
}

function getProcessingMilestones(
  steps: EvidenceStep[],
  phase: ProcessingPhase
): ProcessingMilestone[] {
  if (phase === "success") {
    return PROCESSING_MILESTONES.map((milestone) => ({
      ...milestone,
      status: "success",
    }));
  }

  return PROCESSING_MILESTONES.map((milestone) => ({
    ...milestone,
    status: getMilestoneStatus(milestone.ids, steps),
  }));
}

function getProcessingProgress(
  milestones: ProcessingMilestone[],
  phase: ProcessingPhase
): number {
  if (phase === "success") return 100;
  const completed = milestones.filter(
    (milestone) => milestone.status === "success"
  ).length;
  const runningBonus = milestones.some(
    (milestone) => milestone.status === "running"
  )
    ? 0.5
    : 0;
  return Math.round(
    ((completed + runningBonus) / Math.max(milestones.length, 1)) * 100
  );
}

function redactEvidence(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(redactEvidence);
  if (!value || typeof value !== "object") return value;

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      /(authorization|password|passphrase|secret|api_key|token)/i.test(key)
        ? "[redacted]"
        : redactEvidence(item),
    ])
  );
}

function Logo() {
  return (
    <div className="logoMark" aria-label="Canopy">
      C
    </div>
  );
}

function ProfileCard({
  profile,
  selected,
  onSelect,
}: {
  profile: DemoProfile;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={`profileCard ${selected ? "selected" : ""}`}
      onClick={onSelect}
    >
      <span className={`profileSignal ${profile.tone}`} />
      <strong>{profile.name}</strong>
      <span>{profile.description}</span>
      <small>{profile.expected}</small>
    </button>
  );
}

function StatusPill({ status }: { status: EvidenceStatus }) {
  const labels: Record<EvidenceStatus, string> = {
    pending: "Pendiente",
    running: "Ejecutando",
    success: "OK",
    error: "Error",
  };
  return <span className={`statusPill ${status}`}>{labels[status]}</span>;
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <details className="jsonBlock">
      <summary>{title}</summary>
      <pre>{JSON.stringify(redactEvidence(value ?? null), null, 2)}</pre>
    </details>
  );
}

function EvidenceAccordion({ step }: { step: EvidenceStep }) {
  return (
    <details className="evidenceAccordion">
      <summary>
        <span>
          <strong>{step.label}</strong>
          <small>{step.description}</small>
        </span>
        <StatusPill status={step.status} />
      </summary>
      {step.result ? (
        <div className="evidencePayload">
          <div className="endpointLine">
            <b>{step.result.request?.method ?? "-"}</b>
            <code>{step.result.request?.url ?? "Sin request disponible"}</code>
            <span>{step.result.status}</span>
          </div>
          <JsonBlock title="Request" value={step.result.request} />
          <JsonBlock title="Response" value={step.result.response} />
        </div>
      ) : (
        <p className="emptyEvidence">La llamada aún no se ha ejecutado.</p>
      )}
    </details>
  );
}

function EvidenceSidebar({
  steps,
  consentActionId,
  pdfReady,
  receiptDownloadBusy,
  receiptMessage,
  onDownload,
}: {
  steps: EvidenceStep[];
  consentActionId?: string;
  pdfReady: boolean;
  receiptDownloadBusy: boolean;
  receiptMessage: string;
  onDownload: () => void;
}) {
  return (
    <aside className="evidenceSidebar">
      <div className="sidebarHeader">
        <span className="eyebrow">Evidencia técnica</span>
        <h2>Request / Response</h2>
      </div>
      <div className="accordionList">
        {steps.map((step) => (
          <EvidenceAccordion key={step.id} step={step} />
        ))}
      </div>
      <div className={`receiptAction ${pdfReady ? "ready" : ""}`}>
        <div>
          <strong>Recibo PDF</strong>
          <span>
            {pdfReady
              ? "Disponible para descarga"
              : consentActionId
              ? "Soyio está preparando el recibo; puedes intentar descargarlo."
              : "Disponible después de registrar el consentimiento"}
          </span>
        </div>
        <button
          type="button"
          className="secondaryCta"
          disabled={!consentActionId || receiptDownloadBusy}
          onClick={onDownload}
        >
          {receiptDownloadBusy ? "Preparando PDF..." : "Descargar PDF"}
        </button>
        {receiptMessage && <p>{receiptMessage}</p>}
      </div>
    </aside>
  );
}

function EvidenceDisclosure({
  steps,
  isOpen,
  onToggleOpen,
}: {
  steps: EvidenceStep[];
  isOpen: boolean;
  onToggleOpen: (isOpen: boolean) => void;
}) {
  const successCount = steps.filter((step) => step.status === "success").length;
  const errorCount = steps.filter((step) => step.status === "error").length;
  const pendingCount = steps.length - successCount - errorCount;

  return (
    <details
      className="evidenceDisclosure"
      open={isOpen}
      onToggle={(event) =>
        onToggleOpen((event.currentTarget as HTMLDetailsElement).open)
      }
    >
      <summary>
        <span>
          <span className="eyebrow">Evidencia técnica</span>
          <strong>Ver evidencia técnica de la operación</strong>
          <small>
            {successCount} OK · {pendingCount} pendientes · {errorCount} errores
          </small>
        </span>
        <span className="disclosureHint">
          {isOpen ? "Colapsar" : "Expandir"}
        </span>
      </summary>
      <div className="evidenceDisclosureBody">
        <div className="accordionList">
          {steps.map((step) => (
            <EvidenceAccordion key={step.id} step={step} />
          ))}
        </div>
      </div>
    </details>
  );
}

function FinalResultToolbar({
  canDownloadReceipt,
  canPrintReceipt,
  receiptDownloadBusy,
  receiptMessage,
  onDownloadReceipt,
  onPrintReceipt,
  onRestart,
}: {
  canDownloadReceipt: boolean;
  canPrintReceipt: boolean;
  receiptDownloadBusy: boolean;
  receiptMessage: string;
  onDownloadReceipt: () => void;
  onPrintReceipt: () => void;
  onRestart: () => void;
}) {
  return (
    <section className="finalResultToolbar">
      <div>
        <span className="eyebrow">Resultado listo</span>
        <strong>Consulta REDEC completada</strong>
        {receiptMessage && <p>{receiptMessage}</p>}
      </div>
      <div className="finalToolbarActions">
        <button
          className="secondaryCta"
          type="button"
          disabled={!canDownloadReceipt || receiptDownloadBusy}
          onClick={onDownloadReceipt}
        >
          {receiptDownloadBusy
            ? "Preparando PDF..."
            : "Descargar PDF consentimiento otorgado"}
        </button>
        <button
          className="printConsentCta"
          type="button"
          disabled={!canPrintReceipt}
          onClick={onPrintReceipt}
        >
          Imprimir consentimiento Soyio
        </button>
        <button className="secondaryCta" type="button" onClick={onRestart}>
          Probar otro perfil
        </button>
      </div>
    </section>
  );
}

function ThermalConsentReceipt({
  consentAction,
  agreement,
}: {
  consentAction: ConsentAction | null;
  agreement: Agreement | null;
}) {
  const receiptHash = consentAction?.redec_pdf_hash ?? "-";
  const durationSummary = agreementDurationSummary(agreement);
  const qrPayload = [
    "soyio-redec",
    consentAction?.id ?? "pending",
    consentAction?.redec_pdf_hash ?? "pending",
  ].join("|");
  const qrCode = useMemo(
    () =>
      QRCode.create(qrPayload, {
        errorCorrectionLevel: "H",
      }),
    [qrPayload]
  );
  const qrMargin = 2;
  const qrSize = qrCode.modules.size + qrMargin * 2;

  return (
    <section className="thermalPrintArea" aria-hidden="true">
      <div className="thermalReceipt">
        <header className="thermalQrBlock">
          <svg
            className="thermalQr"
            viewBox={`0 0 ${qrSize} ${qrSize}`}
            role="img"
            aria-label="QR de verificación del consentimiento"
            shapeRendering="crispEdges"
          >
            <rect width={qrSize} height={qrSize} fill="#ffffff" />
            {Array.from({ length: qrCode.modules.size }).flatMap((_, row) =>
              Array.from({ length: qrCode.modules.size }).map((__, col) =>
                qrCode.modules.get(row, col) ? (
                  <rect
                    key={`${row}-${col}`}
                    x={col + qrMargin}
                    y={row + qrMargin}
                    width="1"
                    height="1"
                    fill="#000000"
                  />
                ) : null
              )
            )}
          </svg>
          <div className="thermalQrMark">
            <img src="/soyio_icon.png" alt="" />
          </div>
        </header>
        <div className="thermalRule" />
        <h1>Copia de tu consentimiento</h1>
        <p className="thermalLead">
          Canopy registró el consentimiento que otorgaste para consultar tus
          datos personales de deuda almacenados en el Registro de Deuda
          Consolidada (REDEC).
        </p>
        <p className="thermalLead">
          Estos datos solo podrán ser usados para Evaluar el riesgo comercial de
          la operación solicitada. Este correo no solicita permisos
          adicionales ni autoriza usos distintos a los indicados.
        </p>
        <dl className="thermalDetails">
          <div>
            <dt>Fecha</dt>
            <dd>{formatReceiptDateTime(consentAction?.created_at)}</dd>
          </div>
          <div>
            <dt>Datos</dt>
            <dd>Datos personales de deuda almacenados en REDEC</dd>
          </div>
          <div>
            <dt>Finalidad</dt>
            <dd>Evaluar el riesgo comercial de la operación solicitada</dd>
          </div>
          <div>
            <dt>Vigencia</dt>
            <dd>{durationSummary}</dd>
          </div>
          <div>
            <dt>Cláusula</dt>
            <dd>
              Autorizo a Canopy a utilizar mis datos personales para realizar la
              evaluación crediticia y las respectivas consultas al registro de
              deuda consolidada y boletín comercial, con el objeto de generar la
              solicitud de productos en caso de que proceda conforme a lo
              dispuesto en la normativa vigente.
            </dd>
          </div>
        </dl>
        <div className="thermalRule" />
        <section className="thermalEvidence">
          <h2>Transacción</h2>
          <p>{consentAction?.id ?? "-"}</p>
          <h2>SHA256</h2>
          <p>{receiptHash}</p>
        </section>
        <strong className="thermalTrace">
          Consentimiento con trazabilidad y evidencia verificable
        </strong>
        <footer className="thermalBackedBy">
          <span>Respaldado con</span>
          <div
            className="thermalSoyioLogo thermalSoyioLogoSmall"
            aria-label="Soyio"
          >
            <span className="thermalSoyioIcon">
              <img src="/soyio_icon.png" alt="" />
            </span>
            <strong>soy io</strong>
          </div>
        </footer>
      </div>
    </section>
  );
}

function EvaluationError({ message }: { message: string }) {
  return (
    <div className="globalError">
      <div>
        <strong>No pudimos completar la evaluación</strong>
        <span>{message}</span>
      </div>
    </div>
  );
}

function Header() {
  return (
    <header>
        <div className="brand">
          <Logo />
          <div>
            <strong>Canopy - Demo evaluación crediticia con REDEC </strong>
            <span>Captura consentimiento con Soyio, consulta deuda redec en la CMF y genera evidencia auditable.</span>
          </div>
        </div>
    </header>
  );
}

function ProfileStage({
  profile,
  email,
  selectedRut,
  userReference,
  onProfileChange,
  onEmailChange,
  onContinue,
}: {
  profile: DemoProfile;
  email: string;
  selectedRut: string;
  userReference: string;
  onProfileChange: (profile: DemoProfile) => void;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
}) {
  return (
    <section className="stageCard soloStage">
      <h2>Selecciona perfil sandbox</h2>
      <p className="stageCopy">
        Elige un RUT de prueba y agrega un email opcional para simular
        notificación o recibo.
      </p>
      <div className="profileGrid">
        {PROFILES.map((item) => (
          <ProfileCard
            key={item.id}
            profile={item}
            selected={item.id === profile.id}
            onSelect={() => onProfileChange(item)}
          />
        ))}
      </div>
      <label className="fieldLabel">
        Email opcional
        <input
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          type="email"
          placeholder="cliente@empresa.cl"
        />
      </label>
      <p className="quietMeta">
        Sandbox · RUT {selectedRut} · referencia {userReference}
      </p>
      <button className="primaryCta" type="button" onClick={onContinue}>
        Continuar a consentimiento
      </button>
    </section>
  );
}

function ConsentStage({
  consent,
  busy,
  onBack,
  onStart,
}: {
  consent: ConsentCheckboxChange | null;
  busy: boolean;
  onBack: () => void;
  onStart: () => void;
}) {
  return (
    <section className="stageCard soloStage">
      <h2>Autoriza la consulta REDEC</h2>
      <p className="stageCopy">
        El SDK de Soyio ayuda al usuario a entender el consentimiento REDEC.
      </p>
      <div id="soyio-consent" className="soyioMount" />
      <div className="stageActions">
        <button className="secondaryCta" type="button" onClick={onBack}>
          Volver
        </button>
        <button
          type="button"
          className="primaryCta"
          disabled={!consent?.actionToken || busy}
          onClick={onStart}
        >
          {busy ? "Iniciando..." : "Iniciar evaluación"}
        </button>
      </div>
      {!consent?.actionToken && (
        <p className="helperText">
          Acepta el consentimiento para habilitar la evaluación.
        </p>
      )}
    </section>
  );
}

function ProcessingStage({
  message,
  phase,
  progress,
  milestones,
}: {
  message: string;
  phase: ProcessingPhase;
  progress: number;
  milestones: ProcessingMilestone[];
}) {
  const currentMilestone =
    milestones.find((milestone) => milestone.status === "running") ??
    milestones.find((milestone) => milestone.status === "pending") ??
    milestones[milestones.length - 1];

  return (
    <section className={`stageCard processingStage ${phase}`}>
      <div className="processingHero">
        <p className="eyebrow">
          {phase === "success" ? "Logrado" : "Generando evidencia"}
        </p>
        <div className="successCheck" aria-hidden="true">
          <span />
        </div>
        <h2>
          {phase === "success"
            ? "Operación REDEC completada"
            : currentMilestone?.label ?? "Estamos ejecutando el flujo REDEC"}
        </h2>
        <p className="stageCopy">
          {phase === "success"
            ? "Revisemos el registro de deuda consolidada del usuario consultado."
            : message || "Preparando consentimiento, compliance y consulta CMF..."}
        </p>
      </div>
      <div className="progressPanel">
        <div className="progressHeader">
          <span>Progreso de evidencia</span>
          <strong>{progress}%</strong>
        </div>
        <div className="progressTrack" aria-hidden="true">
          <span style={{ width: `${progress}%` }} />
        </div>
        <div className="milestoneList">
          {milestones.map((milestone) => (
            <div
              className={`milestoneItem ${milestone.status}`}
              key={milestone.label}
            >
              <span className="milestoneDot" />
              <strong>{milestone.label}</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ResultStage({
  summary,
  selectedRut,
  accessReady,
  isOpen,
  onToggleOpen,
}: {
  summary: ReturnType<typeof debtSummary> | null;
  selectedRut: string;
  accessReady: boolean;
  isOpen: boolean;
  onToggleOpen: (isOpen: boolean) => void;
}) {
  const rowsPerPage = 10;
  const [debtPage, setDebtPage] = useState(1);

  if (!summary) {
    return (
      <section className="stageCard resultStage">
        <p className="eyebrow">Resultado CMF snapshot</p>
        <h2>Sin resultado disponible</h2>
      </section>
    );
  }

  const totalDebtRows = summary.rows.length;
  const sortedDebtRows = [...summary.rows].sort((a, b) => b.mora - a.mora);
  const debtPageCount = Math.max(Math.ceil(totalDebtRows / rowsPerPage), 1);
  const safeDebtPage = Math.min(debtPage, debtPageCount);
  const firstDebtRow = (safeDebtPage - 1) * rowsPerPage;
  const visibleDebtRows = sortedDebtRows.slice(
    firstDebtRow,
    firstDebtRow + rowsPerPage
  );

  return (
    <details
      className="stageCard resultStage"
      open={isOpen}
      onToggle={(event) =>
        onToggleOpen((event.currentTarget as HTMLDetailsElement).open)
      }
    >
      <summary className="resultSummary">
        <div>
          <p className="eyebrow">Resultado CMF snapshot</p>
          <h3>Deuda consolidada del RUT {summary.rut ?? selectedRut}</h3>
          <p>
            Periodo {summary.periodoInicio ?? "-"} a{" "}
            {summary.periodoFin ?? "-"}. Acceso RDC31{" "}
            {accessReady ? "registrado" : "pendiente"}.
          </p>
        </div>
        <span className="disclosureHint">
          {isOpen ? "Colapsar" : "Expandir"}
        </span>
      </summary>
      <div className="resultContent">
        <div className="metricGrid heroMetrics">
          <div>
            <span>Deuda vigente</span>
            <strong>{formatMoney(summary.deuda)}</strong>
          </div>
          <div>
            <span>Mora acumulada</span>
            <strong>{formatMoney(summary.mora)}</strong>
          </div>
          <div>
            <span>Operaciones</span>
            <strong>{formatNumber(summary.operaciones)}</strong>
          </div>
          <div>
            <span>Periodos con mora</span>
            <strong>{formatNumber(summary.periodosConMora)}</strong>
          </div>
        </div>
        {totalDebtRows > 0 ? (
          <div className="debtTableSection">
            <div className="debtTableHeader">
              <div>
                <h4>Registros de deuda</h4>
                <p>
                  Mostrando {firstDebtRow + 1}-
                  {Math.min(firstDebtRow + rowsPerPage, totalDebtRows)} de{" "}
                  {totalDebtRows} registros devueltos por CMF.
                </p>
              </div>
              {debtPageCount > 1 && (
                <div className="paginationControls">
                  <button
                    className="secondaryCta"
                    type="button"
                    disabled={safeDebtPage === 1}
                    onClick={() =>
                      setDebtPage((current) => Math.max(current - 1, 1))
                    }
                  >
                    Anterior
                  </button>
                  <span>
                    Página {safeDebtPage} de {debtPageCount}
                  </span>
                  <button
                    className="secondaryCta"
                    type="button"
                    disabled={safeDebtPage === debtPageCount}
                    onClick={() =>
                      setDebtPage((current) =>
                        Math.min(current + 1, debtPageCount)
                      )
                    }
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
            <div className="debtTableWrap">
              <table>
                <thead>
                  <tr>
                    <th>Periodo</th>
                    <th>Crédito</th>
                    <th>Deuda</th>
                    <th>Al día</th>
                    <th>Mora</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleDebtRows.map((row, index) => (
                    <tr key={`${row.periodo}-${firstDebtRow + index}`}>
                      <td>{row.periodo}</td>
                      <td>
                        {row.tipoCredito} · {row.tipoDeudor}
                      </td>
                      <td>{formatMoney(row.deudaVigente)}</td>
                      <td>{formatMoney(row.montoAlDia)}</td>
                      <td>{formatMoney(row.mora)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="noDebtState">
            <strong>Sin registros de deuda</strong>
            <p>{summary.message ?? "CMF no retornó deudas para este perfil sandbox."}</p>
          </div>
        )}
      </div>
    </details>
  );
}

export function App() {
  const [stage, setStage] = useState<Stage>("profile");
  const [profile, setProfile] = useState<DemoProfile>(PROFILES[1]);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState<ConsentCheckboxChange | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState("");
  const [processingPhase, setProcessingPhase] =
    useState<ProcessingPhase>("running");
  const [receiptDownloadBusy, setReceiptDownloadBusy] = useState(false);
  const [receiptMessage, setReceiptMessage] = useState("");
  const [consentAction, setConsentAction] = useState<ConsentAction | null>(null);
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [pdfReady, setPdfReady] = useState(false);
  const [complianceOk, setComplianceOk] = useState(false);
  const [debtResult, setDebtResult] = useState<ApiResult | null>(null);
  const [accessReady, setAccessReady] = useState(false);
  const [isCmfResultOpen, setIsCmfResultOpen] = useState(false);
  const [isEvidenceOpen, setIsEvidenceOpen] = useState(false);
  const [evidenceSteps, setEvidenceSteps] =
    useState<EvidenceStep[]>(EVIDENCE_TEMPLATE);

  useEffect(() => {
    if (stage !== "consent") {
      destroyConsentBox();
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      mountConsentBox({
        templateId: REDEC_CONSENT_TEMPLATE_ID as `constpl_${string}`,
        containerSelector: "#soyio-consent",
        onCheckboxChange: setConsent,
      });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      destroyConsentBox();
    };
  }, [stage]);

  useEffect(() => {
    if (consentAction?.redec_pdf_hash) setPdfReady(true);
  }, [consentAction?.redec_pdf_hash]);

  const selectedRut = normalizeRut(profile.rut);
  const userReference = userReferenceForRut(selectedRut);
  const summary = debtResult?.ok ? debtSummary(debtResult.response) : null;
  const processingMilestones = getProcessingMilestones(
    evidenceSteps,
    processingPhase
  );
  const processingProgress = getProcessingProgress(
    processingMilestones,
    processingPhase
  );
  const showEvidenceSidebar = false;
  const showEvidenceDisclosure = stage === "result" && accessReady;

  const resetFlow = () => {
    setStage("profile");
    setConsent(null);
    setBusy(false);
    setError(null);
    setProcessingMessage("");
    setProcessingPhase("running");
    setReceiptMessage("");
    setConsentAction(null);
    setAgreement(null);
    setPdfReady(false);
    setComplianceOk(false);
    setDebtResult(null);
    setAccessReady(false);
    setIsCmfResultOpen(false);
    setIsEvidenceOpen(false);
    setEvidenceSteps(EVIDENCE_TEMPLATE);
  };

  const updateEvidence = (
    id: EvidenceStepId,
    patch: Partial<Omit<EvidenceStep, "id">>
  ) => {
    setEvidenceSteps((current) =>
      current.map((step) => (step.id === id ? { ...step, ...patch } : step))
    );
  };

  const markRunning = (id: EvidenceStepId) =>
    updateEvidence(id, { status: "running" });

  const markResult = (id: EvidenceStepId, result: ApiResult) =>
    updateEvidence(id, { status: result.ok ? "success" : "error", result });

  const buildCmfPayload = (
    action: ConsentAction,
    agreementData: Agreement,
    hash: string
  ): CmfRedecDebtPayload => {
    const createdAt = action.created_at;
    if (!createdAt) throw new Error("No encontramos la fecha del consentimiento.");
    const start = new Date(createdAt);
    if (Number.isNaN(start.getTime())) {
      throw new Error("No pudimos interpretar la fecha del consentimiento.");
    }

    const expiresAt = getAgreementExpiresAt(agreementData);
    const end = expiresAt ? new Date(expiresAt) : new Date(start);
    if (!expiresAt) end.setDate(end.getDate() + 15);
    if (Number.isNaN(end.getTime())) {
      throw new Error("No pudimos interpretar la vigencia del acuerdo.");
    }

    return {
      codigo_interno: "22222",
      codigo_institucion: normalizeRut(CMF_CODIGO_INSTITUCION),
      rut_consultado: selectedRut,
      medio_consentimiento: 1,
      objetivo_consentimiento: [1],
      finalidad_consentimiento: 2,
      fecha_inicio_otorgamiento: formatCmfDate(start),
      hora_inicio_otorgamiento: formatCmfTime(start),
      fecha_fin_consentimiento: formatCmfDate(end),
      hora_fin_consentimiento: formatCmfTime(end),
      hash: {
        algoritmo: "SHA-256",
        hash,
      },
    };
  };

  const waitForReceiptHash = async (
    initialAction: ConsentAction
  ): Promise<ConsentAction> => {
    if (!initialAction.id) {
      throw new Error("No encontramos el consentimiento para generar el PDF.");
    }

    const actionId = initialAction.id;
    let action = initialAction;
    if (action.redec_pdf_hash) {
      setPdfReady(true);
      updateEvidence("receipt", { status: "success" });
      return action;
    }

    const messages = [
      "Generando PDF del consentimiento...",
      "Haciendo la encriptación del recibo...",
      "Validando el hash para la consulta CMF...",
      "Sincronizando evidencia Soyio...",
      "Ya casi está listo, confirmando el hash del PDF...",
    ];

    markRunning("receipt");
    for (let attempt = 0; attempt < 12; attempt += 1) {
      setProcessingMessage(messages[Math.min(attempt, messages.length - 1)]);

      const receipt = await downloadRedecReceiptPdf(actionId, {
        download: false,
      });
      if (isReceiptPending(receipt)) {
        updateEvidence("receipt", { status: "running", result: receipt });
      } else {
        markResult("receipt", receipt);
      }
      if (receipt.ok) setPdfReady(true);
      if (!receipt.ok && !isReceiptPending(receipt)) {
        throw new Error("No pudimos preparar el recibo PDF REDEC.");
      }

      await wait(attempt < 4 ? 1600 : 2600);

      const refreshed = await getRedecConsentAction(actionId);
      markResult("consent_action", refreshed);
      if (!refreshed.ok || !refreshed.response.consent_action) continue;

      action = refreshed.response.consent_action;
      setConsentAction(action);
      if (action.redec_pdf_hash) {
        setPdfReady(true);
        updateEvidence("receipt", { status: "success", result: receipt });
        setProcessingMessage("Recibo cifrado listo. Continuamos con compliance.");
        return action;
      }
    }

    throw new Error(
      "El recibo PDF sigue en preparación. Intenta nuevamente en unos segundos."
    );
  };

  const downloadReceiptForUser = async () => {
    if (!consentAction?.id || receiptDownloadBusy) return;

    setReceiptDownloadBusy(true);
    setReceiptMessage("Preparando descarga del recibo PDF...");
    try {
      const result = await downloadRedecReceiptPdf(consentAction.id, {
        download: true,
      });
      markResult("receipt", result);
      if (!result.ok) {
        throw new Error(
          isReceiptPending(result)
            ? "El recibo todavía se está generando. Vuelve a intentar en unos segundos."
            : "No pudimos descargar el recibo PDF."
        );
      }
      setPdfReady(true);
      setReceiptMessage("Recibo PDF descargado.");
    } catch (e) {
      setReceiptMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setReceiptDownloadBusy(false);
    }
  };

  const printSoyioConsent = () => {
    if (!consentAction?.id || !consentAction.redec_pdf_hash) {
      setReceiptMessage(
        "El comprobante Soyio estará listo cuando el recibo PDF tenga hash."
      );
      return;
    }

    setReceiptMessage("Abriendo impresión térmica del consentimiento Soyio...");
    window.requestAnimationFrame(() => window.print());
  };

  const completeCmfAndAccess = async (context: CmfRetryContext) => {
    setProcessingMessage("Cargando snapshot real CMF para el perfil...");
    markRunning("cmf");
    const debt = await consultCmfRedecDebt(context.payload);
    markResult("cmf", debt);
    if (!debt.ok) {
      const message =
        typeof debt.response?.message === "string"
          ? debt.response.message
          : "No pudimos cargar el snapshot CMF local para este perfil.";
      throw new Error(message);
    }

    setDebtResult(debt);

    setProcessingMessage("Registrando acceso RDC31 para auditoría...");
    const redecId = context.action.redec_data?.redec_id;
    const access = accessFromConsentCreatedAt(context.action.created_at);
    if (!redecId || !access) {
      throw new Error("No pudimos preparar el registro RDC31.");
    }

    markRunning("access");
    const accessResult = await createRedecAccess({
      redec_id: redecId,
      access_date: access.date,
      access_time: access.time,
    });
    markResult("access", accessResult);
    if (!accessResult.ok) {
      throw new Error("No pudimos registrar el acceso RDC31.");
    }
    setAccessReady(true);
    setProcessingMessage("Evaluación completada con evidencia lista.");
    setProcessingPhase("success");
    await wait(3000);
    setStage("result");
  };

  const runEvaluation = async () => {
    if (!consent?.actionToken || busy) return;

    setStage("processing");
    setProcessingPhase("running");
    setBusy(true);
    setError(null);
    setConsentAction(null);
    setAgreement(null);
    setPdfReady(false);
    setComplianceOk(false);
    setDebtResult(null);
    setAccessReady(false);
    setIsCmfResultOpen(false);
    setIsEvidenceOpen(false);
    setProcessingMessage("Validando identidad de prueba en Soyio...");
    setReceiptMessage("");
    setEvidenceSteps(EVIDENCE_TEMPLATE);

    try {
      markRunning("entity");
      const entityCheck = await getEntitiesByUserReference(userReference);
      markResult("entity", entityCheck);
      if (!entityCheck.ok) {
        throw new Error(
          "No pudimos validar la entidad Soyio asociada al RUT de prueba."
        );
      }

      setProcessingMessage("Registrando consentimiento REDEC en Soyio...");
      markRunning("register");
      let registration = await createRedecConsentAction({
        actionToken: consent.actionToken,
        entityNin: selectedRut,
        email: email.trim() || undefined,
        userReference,
      });

      if (!registration.ok && hasNinMismatch(registration)) {
        const fallbackReference = retryUserReferenceForRut(selectedRut);
        const fallbackEntityCheck = await getEntitiesByUserReference(
          fallbackReference
        );
        markResult("entity", fallbackEntityCheck);
        if (!fallbackEntityCheck.ok) {
          throw new Error(
            "No pudimos validar una nueva entidad Soyio para este RUT."
          );
        }
        setProcessingMessage("Creando una entidad limpia para este RUT...");
        registration = await createRedecConsentAction({
          actionToken: consent.actionToken,
          entityNin: selectedRut,
          email: email.trim() || undefined,
          userReference: fallbackReference,
        });
      }
      markResult("register", registration);

      if (!registration.ok || !registration.response.consent_action?.id) {
        const details = apiErrorMessages(registration).join(" ");
        throw new Error(
          details
            ? `No pudimos registrar el consentimiento REDEC: ${details}`
            : "No pudimos registrar el consentimiento REDEC."
        );
      }

      const registeredAction = registration.response.consent_action;
      const registeredActionId = registeredAction.id as string;
      setConsentAction(registeredAction);

      setProcessingMessage("Consultando el consentimiento registrado...");
      markRunning("consent_action");
      const actionResult = await getRedecConsentAction(registeredActionId);
      markResult("consent_action", actionResult);
      if (!actionResult.ok || !actionResult.response.consent_action) {
        throw new Error("No pudimos consultar el consentimiento REDEC.");
      }

      let action = actionResult.response.consent_action;
      setConsentAction(action);

      if (!action.agreement_id) {
        throw new Error("No encontramos el agreement asociado.");
      }
      setProcessingMessage("Consultando agreement y vigencia del permiso...");
      markRunning("agreement");
      const agreementResult = await getAgreement(action.agreement_id);
      markResult("agreement", agreementResult);
      if (!agreementResult.ok || !agreementResult.response.agreement) {
        throw new Error("No pudimos consultar el agreement.");
      }
      const agreementData = agreementResult.response.agreement;
      setAgreement(agreementData);

      action = await waitForReceiptHash(action);

      if (!action.entity_id) {
        throw new Error("No encontramos la entidad del consentimiento.");
      }
      setProcessingMessage("Verificando compliance para consultar deuda...");
      markRunning("compliance");
      const compliance = await getComplianceStatuses(
        action.entity_id,
        REDEC_CONSENT_TEMPLATE_ID
      );
      markResult("compliance", compliance);
      if (!compliance.ok || !isCompliant(compliance.response)) {
        throw new Error(
          "No encontramos compliance vigente para consultar deuda REDEC."
        );
      }
      setComplianceOk(true);

      const receiptHash = action.redec_pdf_hash;
      if (!receiptHash) {
        throw new Error(
          "El recibo PDF sigue en preparación. Intenta nuevamente en unos segundos."
        );
      }
      setProcessingMessage("Preparando payload y snapshot CMF...");
      const cmfPayload = buildCmfPayload(action, agreementData, receiptHash);
      await completeCmfAndAccess({ action, payload: cmfPayload });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const mainContent = useMemo(() => {
    if (stage === "profile") {
      return (
        <ProfileStage
          profile={profile}
          email={email}
          selectedRut={selectedRut}
          userReference={userReference}
          onProfileChange={setProfile}
          onEmailChange={setEmail}
          onContinue={() => setStage("consent")}
        />
      );
    }

    if (stage === "consent") {
      return (
        <ConsentStage
          consent={consent}
          busy={busy}
          onBack={() => setStage("profile")}
          onStart={runEvaluation}
        />
      );
    }

    if (stage === "processing") {
      return (
        <ProcessingStage
          message={processingMessage}
          phase={processingPhase}
          progress={processingProgress}
          milestones={processingMilestones}
        />
      );
    }

    return (
      <ResultStage
        summary={summary}
        selectedRut={selectedRut}
        accessReady={accessReady}
        isOpen={isCmfResultOpen}
        onToggleOpen={setIsCmfResultOpen}
      />
    );
  }, [
    accessReady,
    busy,
    consent,
    email,
    isCmfResultOpen,
    processingMessage,
    processingMilestones,
    processingPhase,
    processingProgress,
    profile,
    selectedRut,
    stage,
    summary,
    userReference,
  ]);

  return (
    <main className="appShell">
      <Header />
      {error && (
        <EvaluationError message={error} />
      )}
      <section
        className={`stageLayout ${
          showEvidenceSidebar ? "withEvidence" : ""
        }`}
      >
        <div>
          {showEvidenceDisclosure && (
            <FinalResultToolbar
              canDownloadReceipt={Boolean(consentAction?.id)}
              canPrintReceipt={Boolean(
                consentAction?.id && consentAction.redec_pdf_hash
              )}
              receiptDownloadBusy={receiptDownloadBusy}
              receiptMessage={receiptMessage}
              onDownloadReceipt={downloadReceiptForUser}
              onPrintReceipt={printSoyioConsent}
              onRestart={resetFlow}
            />
          )}
          {mainContent}
          {showEvidenceDisclosure && (
            <EvidenceDisclosure
              steps={evidenceSteps}
              isOpen={isEvidenceOpen}
              onToggleOpen={setIsEvidenceOpen}
            />
          )}
        </div>
        {showEvidenceSidebar && (
          <EvidenceSidebar
            steps={evidenceSteps}
            consentActionId={consentAction?.id}
            pdfReady={pdfReady}
            receiptDownloadBusy={receiptDownloadBusy}
            receiptMessage={receiptMessage}
            onDownload={downloadReceiptForUser}
          />
        )}
      </section>
      <ThermalConsentReceipt
        consentAction={consentAction}
        agreement={agreement}
      />
      <footer className="footerNote">
        Demo sandbox. Los datos corresponden a perfiles de prueba para mostrar
        consentimiento REDEC, consulta CMF y registro RDC31.
      </footer>
    </main>
  );
}
